import { Autocomplete, Box, Grid, MenuItem, Typography } from "@mui/material";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import {
  fetchExpertUsers,
  selectActiveExpertUserOptions,
} from "../../user/expertUsersSlice";
import { selectAllCustomerContracts } from "../customerWorkLocationsSlice";
import { NAME_SPACE } from "./WorkLocationsForm";

const BasicInfo = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const dispatch = useAppDispatch();

  const contractOptions = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  );

  useEffect(() => {
    dispatch(fetchExpertUsers({}));
  }, [dispatch]);

  const coreUserOptions = useAppSelector((state) => selectActiveExpertUserOptions(state));

  return (
    <FormSection>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography>{t(`${NAME_SPACE}:workLocationName`)}</Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          control={control}
          name="name"
          rules={{ required: true }}
          render={({ field }) => (
            <CommonTextField
              {...field}
              sx={{ maxWidth: "372px" }}
              fullWidth
              error={!!errors.name}
            />
          )}
        />
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography>{t(`${NAME_SPACE}:customerContract`)}</Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          control={control}
          name="contract"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <CommonTextField
              sx={{ maxWidth: "372px" }}
              value={value?.id || ""}
              onChange={(e) => onChange({ id: e.target.value })}
              fullWidth
              select
              error={!!errors.contract}
            >
              {contractOptions.map((contract) => (
                <MenuItem key={contract.id} value={contract.id}>
                  {contract.name}
                </MenuItem>
              ))}
            </CommonTextField>
          )}
        />
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography>{t(`${NAME_SPACE}:salesPerson`)}</Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          control={control}
          name="salesPerson"
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              sx={{ maxWidth: "372px" }}
              value={value || null}
              options={coreUserOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => onChange(newValue)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => <CommonTextField {...params} />}
            />
          )}
        />
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end">
        <Typography marginTop="0.25rem" textAlign="right">
          {t(`${NAME_SPACE}:additionalInfoOptional`)}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          control={control}
          name="descriptionForEezy"
          render={({ field }) => (
            <Box display="flex" gap="1rem">
              <CommonTextField
                {...field}
                multiline
                minRows={4}
                maxRows={4}
                sx={{ maxWidth: "372px" }}
                fullWidth
              />
              <InfoTooltip title={t(`${NAME_SPACE}:additionalInfoTooltip`)} />
            </Box>
          )}
        />
      </Grid>
    </FormSection>
  );
};

export default BasicInfo;
